import { Permission } from "@/types/permission";

/**
 * All possible actions in the system - centralized configuration
 */
export const ALL_POSSIBLE_ACTIONS = [
  "read",
  "create",
  "update",
  "delete",
  "export",
  "activate_account",
  "deactivate_account",
  "send_questionnaire_link",
  "reject",
  "confirm",
  "complete",
  "amend",
  "comment",
  "cancel",
  "start",
] as const;

export type PossibleAction = (typeof ALL_POSSIBLE_ACTIONS)[number];

export interface ParsedPermission {
  module: string;
  action: string;
  hasPermission: boolean;
  permission: Permission;
}

export type GroupedPermissions = Record<
  string,
  {
    moduleName: string;
    permissions: ParsedPermission[];
  }
>;

/**
 * Parse permission code to extract module and action
 */
export const parsePermissionCode = (
  code: string,
): { module: string; action: string } | null => {
  // Try to find a known action at the end of the code
  for (const action of ALL_POSSIBLE_ACTIONS) {
    if (code.endsWith(`_${action}`)) {
      const module = code.slice(0, -(action.length + 1)).replace(/_/g, "-");
      return { module, action };
    }
  }

  // Fallback if no known action found
  const parts = code.split("_");
  if (parts.length < 2) return null;

  const action = parts[parts.length - 1];
  const moduleParts = parts.slice(0, -1);
  const module = moduleParts.join("-");

  return { module, action };
};

/**
 * Group permissions by modules - show only modules with actual permissions
 */
export const groupPermissionsByModule = (
  permissions: Permission[],
  getModuleDisplayName: (module: string) => string,
): GroupedPermissions => {
  const groups: GroupedPermissions = {};

  // Group permissions by module
  const moduleGroups = new Map<string, Permission[]>();

  permissions.forEach((permission) => {
    const parsed = parsePermissionCode(permission.code);
    if (!parsed) return;

    const { module } = parsed;
    if (!moduleGroups.has(module)) {
      moduleGroups.set(module, []);
    }
    const modulePermissions = moduleGroups.get(module);
    if (modulePermissions) {
      modulePermissions.push(permission);
    }
  });

  // Create groups only for modules that have permissions
  moduleGroups.forEach((modulePermissions, module) => {
    const permissionsList = modulePermissions.map((permission) => {
      const parsed = parsePermissionCode(permission.code);
      return {
        module,
        action: parsed?.action || "",
        hasPermission: true,
        permission,
      };
    });

    groups[module] = {
      moduleName: getModuleDisplayName(module),
      permissions: permissionsList,
    };
  });

  return groups;
};

/**
 * Get all unique actions from permissions matrix
 */
export const getAllPossibleActions = (): readonly string[] => {
  return ALL_POSSIBLE_ACTIONS;
};
